import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { contractService } from '../../services/contractService';

// Async thunks
export const fetchContracts = createAsyncThunk(
  'contracts/fetchContracts',
  async (_, { rejectWithValue }) => {
    try {
      const contracts = await contractService.getAll();
      return contracts;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la încărcarea contractelor');
    }
  }
);

export const fetchContractById = createAsyncThunk(
  'contracts/fetchContractById',
  async (id, { rejectWithValue }) => {
    try {
      const contract = await contractService.getById(id);
      return contract;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Contractul nu a fost găsit');
    }
  }
);

export const createContract = createAsyncThunk(
  'contracts/createContract',
  async (contractData, { rejectWithValue }) => {
    try {
      const contract = await contractService.create(contractData);
      return contract;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la crearea contractului');
    }
  }
);

export const createBulkContracts = createAsyncThunk(
  'contracts/createBulkContracts',
  async (bulkData, { rejectWithValue }) => {
    try {
      const result = await contractService.createBulk(bulkData);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la crearea contractelor în masă');
    }
  }
);

export const updateContract = createAsyncThunk(
  'contracts/updateContract',
  async ({ id, contractData }, { rejectWithValue }) => {
    try {
      const contract = await contractService.update(id, contractData);
      return contract;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la actualizarea contractului');
    }
  }
);

export const deleteContract = createAsyncThunk(
  'contracts/deleteContract',
  async (id, { rejectWithValue }) => {
    try {
      await contractService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la ștergerea contractului');
    }
  }
);

export const generateContractPdf = createAsyncThunk(
  'contracts/generatePdf',
  async (id, { rejectWithValue }) => {
    try {
      const contract = await contractService.generatePdf(id);
      return contract;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la generarea PDF-ului');
    }
  }
);

export const getContractAccessLink = createAsyncThunk(
  'contracts/getAccessLink',
  async (id, { rejectWithValue }) => {
    try {
      const result = await contractService.getAccessLink(id);
      return { contractId: id, ...result };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la generarea link-ului de acces');
    }
  }
);

export const fetchExpiringContracts = createAsyncThunk(
  'contracts/fetchExpiringContracts',
  async (days = 30, { rejectWithValue }) => {
    try {
      const contracts = await contractService.getExpiringContracts(days);
      return contracts;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la încărcarea contractelor care expiră');
    }
  }
);

export const searchContracts = createAsyncThunk(
  'contracts/searchContracts',
  async (searchTerm, { rejectWithValue }) => {
    try {
      const contracts = await contractService.search(searchTerm);
      return contracts;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la căutarea contractelor');
    }
  }
);

export const terminateContract = createAsyncThunk(
  'contracts/terminateContract',
  async (id, { rejectWithValue }) => {
    try {
      const contract = await contractService.terminate(id);
      return contract;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Eroare la rezilierea contractului');
    }
  }
);

const initialState = {
  contracts: [],
  selectedContract: null,
  expiringContracts: [],
  loading: false,
  error: null,
  searchResults: [],
  searchLoading: false,
  generatingPdf: false,
  accessLinks: {},
};

const contractSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedContract: (state) => {
      state.selectedContract = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    setGeneratingPdf: (state, action) => {
      state.generatingPdf = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contracts
      .addCase(fetchContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = action.payload;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch contract by ID
      .addCase(fetchContractById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContract = action.payload;
      })
      .addCase(fetchContractById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create contract
      .addCase(createContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts.push(action.payload);
      })
      .addCase(createContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create bulk contracts
      .addCase(createBulkContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBulkContracts.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.contracts) {
          state.contracts.push(...action.payload.contracts);
        }
      })
      .addCase(createBulkContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update contract
      .addCase(updateContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContract.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.contracts.findIndex(contract => contract.id === action.payload.id);
        if (index !== -1) {
          state.contracts[index] = action.payload;
        }
        if (state.selectedContract?.id === action.payload.id) {
          state.selectedContract = action.payload;
        }
      })
      .addCase(updateContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete contract
      .addCase(deleteContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContract.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = state.contracts.filter(contract => contract.id !== action.payload);
      })
      .addCase(deleteContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Generate PDF
      .addCase(generateContractPdf.pending, (state) => {
        state.generatingPdf = true;
        state.error = null;
      })
      .addCase(generateContractPdf.fulfilled, (state, action) => {
        state.generatingPdf = false;
        const index = state.contracts.findIndex(contract => contract.id === action.payload.id);
        if (index !== -1) {
          state.contracts[index] = action.payload;
        }
        if (state.selectedContract?.id === action.payload.id) {
          state.selectedContract = action.payload;
        }
      })
      .addCase(generateContractPdf.rejected, (state, action) => {
        state.generatingPdf = false;
        state.error = action.payload;
      })
      // Get access link
      .addCase(getContractAccessLink.fulfilled, (state, action) => {
        state.accessLinks[action.payload.contractId] = action.payload.accessLink;
      })
      // Fetch expiring contracts
      .addCase(fetchExpiringContracts.fulfilled, (state, action) => {
        state.expiringContracts = action.payload;
      })
      // Search contracts
      .addCase(searchContracts.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchContracts.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchContracts.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })
      // Terminate contract
      .addCase(terminateContract.fulfilled, (state, action) => {
        const index = state.contracts.findIndex(contract => contract.id === action.payload.id);
        if (index !== -1) {
          state.contracts[index] = action.payload;
        }
        if (state.selectedContract?.id === action.payload.id) {
          state.selectedContract = action.payload;
        }
      });
  },
});

export const { clearError, clearSelectedContract, clearSearchResults, setGeneratingPdf } = contractSlice.actions;
export default contractSlice.reducer;
